import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import AssignmentIcon from '@mui/icons-material/Assignment';
import { SnackbarAlert } from "../../../../common/SnackbarAlert";
import { colors } from "../../../../ui/colors";

interface PublishedSiteModalProps {
    isOpen: boolean;
    onClose: () => void;
    onUpdateSite?: () => void | Promise<void>;
    onCreateNewSite?: () => void;
    businessUrl?: string;
    slug?: string;
}

export function PublishedSiteModal({
    isOpen,
    onClose,
    onUpdateSite,
    onCreateNewSite,
    businessUrl,
    slug,
}: PublishedSiteModalProps) {
    const [isCopied, setIsCopied] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState<"success" | "error" | "info" | "warning">("success");
    const [snackbarMessage, setSnackbarMessage] = useState("");

    const fullUrl = useMemo(() => `https://${businessUrl}/marketing/${slug}`, [businessUrl, slug]);

    const handleCopyUrl = () => {
        navigator.clipboard.writeText(fullUrl).then(() => {
            setIsCopied(true);
            setTimeout(() => setIsCopied(false), 2000);
        });
    };

    const handleUpdateClick = async () => {
        if (!onUpdateSite || isUpdating) return;

        try {
            setIsUpdating(true);
            await onUpdateSite();

            setSnackbarSeverity("success");
            setSnackbarMessage("Site updated successfully.");
            setSnackbarOpen(true);
        } catch (e) {
            setSnackbarSeverity("error");
            setSnackbarMessage("Failed to update site. Please try again.");
            setSnackbarOpen(true);
        } finally {
            setIsUpdating(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div style={overlayStyle} onClick={onClose}>
            <motion.div
                style={modalStyle}
                onClick={(e) => e.stopPropagation()}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 20 }}
                transition={{ duration: 0.3 }}
            >
                <button onClick={onClose} style={closeButtonStyle} aria-label="Close modal">
                    ✕
                </button>

                <div style={contentStyle}>
                    <h2 style={titleStyle}>You already have a site published</h2>
                    <p style={descriptionStyle}>Choose what you want to do next.</p>

                    <div style={cardsContainerStyle}>
                        {/* Update existing site */}
                        <motion.div style={cardStyle} whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
                            <div style={cardContentStyle}>
                                <h3 style={cardTitleStyle}>Update this site</h3>
                                <p style={cardDescriptionStyle}>Publish your latest changes to your existing URL.</p>

                                <div style={urlBoxStyle}>
                                    <code style={urlCodeStyle}>{fullUrl}</code>
                                    <button
                                        onClick={handleCopyUrl}
                                        style={copyButtonStyle}
                                        title={isCopied ? "Copied!" : "Copy to clipboard"}
                                    >
                                        {isCopied ? "✓" : <AssignmentIcon sx={{ fontSize: 14 }} />}
                                    </button>
                                </div>

                                <motion.button
                                    onClick={handleUpdateClick}
                                    disabled={!onUpdateSite || isUpdating}
                                    style={{
                                        ...actionButtonStyle,
                                        ...primaryActionButtonStyle,
                                        opacity: !onUpdateSite ? 0.6 : 1,
                                        cursor: !onUpdateSite ? "not-allowed" : isUpdating ? "wait" : "pointer",
                                    }}
                                    whileTap={{ scale: 0.98 }}
                                >
                                    {isUpdating ? "Updating…" : "Update site"}
                                </motion.button>
                            </div>
                        </motion.div>

                        {/* Create new site */}
                        <motion.div style={cardStyle} whileHover={{ y: -4 }} transition={{ duration: 0.2 }}>
                            <div style={cardContentStyle}>
                                <h3 style={cardTitleStyle}>Create a new site</h3>
                                <p style={cardDescriptionStyle}>
                                    Create a new URL (new slug). Additional sites may require payment.
                                </p>

                                <button
                                    onClick={onCreateNewSite}
                                    style={{
                                        ...actionButtonStyle,
                                        ...secondaryActionButtonStyle,
                                    }}
                                >
                                    Create new site
                                </button>
                            </div>
                        </motion.div>
                    </div>

                    <div style={footerStyle}>
                        <button onClick={onClose} style={notNowButtonStyle}>
                            Not now
                        </button>
                    </div>
                </div>

                <SnackbarAlert
                    openState={snackbarOpen}
                    setOpenState={setSnackbarOpen}
                    severity={snackbarSeverity}
                    message={snackbarMessage}
                    duration={4000}
                    position="bottom-center"
                    scope="global"
                />
            </motion.div>
        </div>
    );
}

// ============ STYLES ============

const overlayStyle: React.CSSProperties = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: 20,
};

const modalStyle: React.CSSProperties = {
    position: "relative",
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    borderRadius: 16,
    boxShadow:
        "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
    maxWidth: 900,
    minHeight: 500,
    width: "95%",
    display: "flex",
    flexDirection: "column",
    padding: 40,
};

const closeButtonStyle: React.CSSProperties = {
    position: "absolute",
    top: 16,
    right: 16,
    background: "none",
    border: "none",
    fontSize: 24,
    cursor: "pointer",
    color: colors.gray[300],
    transition: "color 0.2s",
    padding: 0,
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
};

const contentStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 32,
    flex: 1,
    minHeight: 0,
};

const titleStyle: React.CSSProperties = {
    fontSize: 28,
    fontWeight: 700,
    color: colors.gray[500],
    margin: 0,
    lineHeight: 1.2,
};

const descriptionStyle: React.CSSProperties = {
    fontSize: 14,
    color: colors.gray[300],
    margin: 0,
    marginTop: -16,
    lineHeight: 1.6,
};

const cardsContainerStyle: React.CSSProperties = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
    gap: 20,
    alignItems: "stretch",
    flex: 1,
    minHeight: 0,
};

const cardStyle: React.CSSProperties = {
    border: `1px solid var(--fx-surface-border, #E2E8F0)`,
    borderRadius: 12,
    padding: 24,
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    display: "flex",
    flexDirection: "column",
    gap: 16,
    transition: "all 0.2s",
    cursor: "pointer",
    height: "100%",
};

const cardContentStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 12,
    flex: 1,
};

const cardTitleStyle: React.CSSProperties = {
    fontSize: 18,
    fontWeight: 600,
    color: colors.gray[500],
    margin: 0,
};

const cardDescriptionStyle: React.CSSProperties = {
    fontSize: 13,
    color: colors.gray[300],
    margin: 0,
    lineHeight: 1.5,
    flex: 1,
};

const urlBoxStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: 8,
    padding: 12,
    backgroundColor: colors.lightGray.base,
    borderRadius: 8,
    border: `1px solid var(--fx-surface-border, #E2E8F0)`,
};

const urlCodeStyle: React.CSSProperties = {
    fontSize: 11,
    color: colors.gray[400],
    fontFamily: '"Courier New", monospace',
    margin: 0,
    wordBreak: "break-all",
    flex: 1,
};

const copyButtonStyle: React.CSSProperties = {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 4,
    fontSize: 14,
    color: colors.gray[300],
    transition: "color 0.2s",
    flexShrink: 0,
};

const actionButtonStyle: React.CSSProperties = {
    width: "100%",
    padding: "12px 24px",
    borderRadius: 10,
    border: "none",
    fontSize: 14,
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.2s",
    marginTop: "auto",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
};

const primaryActionButtonStyle: React.CSSProperties = {
    backgroundColor: colors.orange.base,
    color: "#FFFFFF",
    boxShadow: "0 1px 3px 0 rgba(0, 0, 0, 0.1)",
};

const secondaryActionButtonStyle: React.CSSProperties = {
    backgroundColor: "var(--fx-surface-base, #FFFFFF)",
    color: colors.orange.base,
    border: `2px solid ${colors.orange.base}`,
};

const footerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: -8,
};

const notNowButtonStyle: React.CSSProperties = {
    background: "none",
    border: "none",
    color: colors.gray[300],
    fontSize: 14,
    cursor: "pointer",
    padding: "8px 16px",
    transition: "color 0.2s",
};
